import type { FC } from 'react';

import Pagination from '@/common-components/Pagination';
import ActiveRentalsTable from '@/features/rentals/components/ActiveRentalsTable';
import DisputesList from '@/features/rentals/components/DisputesList';
import ListingCard from '@/features/rentals/components/ListingCard';
import ListingDetailDrawer from '@/features/rentals/components/ListingDetailDrawer';
import RentalsToolbar from '@/features/rentals/components/RentalsToolbar';
import { useRentalsPage } from '@/features/rentals/hooks/useRentalsPage.hook';
import type { RentalTab } from '@/types/rental.types';

const EMPTY_COPY: Record<RentalTab, { icon: string; title: string; sub: string }> = {
  all: { icon: '📦', title: 'No listings yet', sub: 'Listings appear here as students post items.' },
  active: {
    icon: '🔄',
    title: 'No active rentals',
    sub: 'Rentals show up here while an item is out with a borrower.',
  },
  disputes: {
    icon: '🛡️',
    title: 'No disputes',
    sub: 'Damage reports filed by owners will appear here.',
  },
  reported: { icon: '🚨', title: 'No reported listings', sub: 'Nothing is under review right now.' },
  removed: { icon: '🗑️', title: 'No removed listings', sub: 'Removed items can be restored from here.' },
};

const RentalsPage: FC = () => {
  const vm = useRentalsPage();

  const noun = vm.tab === 'active' ? 'rental' : vm.tab === 'disputes' ? 'dispute' : 'listing';
  const empty = EMPTY_COPY[vm.tab];
  const startIndex = vm.total === 0 ? 0 : (vm.page - 1) * 12 + 1;
  const endIndex =
    vm.total === 0
      ? 0
      : startIndex +
        (vm.tab === 'active'
          ? vm.bookings.length
          : vm.tab === 'disputes'
            ? vm.disputes.length
            : vm.listings.length) -
        1;

  return (
    <>
      <div className="flex flex-col gap-6">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-[22px] font-extrabold tracking-tight text-[var(--color-ink)]">
              Rental Management
            </h1>
            <p className="mt-1 text-[13px] text-[var(--color-ink-muted)]">
              {vm.total.toLocaleString()} {noun}
              {vm.total === 1 ? '' : 's'}
              {vm.disputeCount > 0 && vm.tab !== 'disputes'
                ? ` · ${vm.disputeCount} dispute${vm.disputeCount === 1 ? '' : 's'}`
                : ''}
            </p>
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-2 self-start rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-4 py-2 text-[13px] font-semibold text-[var(--color-ink-muted)] transition-colors hover:bg-[var(--color-surface-muted-2)]"
          >
            📥 Export
          </button>
        </header>

        <section className="overflow-hidden rounded-2xl border border-[var(--color-divider)] bg-white shadow-[var(--shadow-card)]">
          <RentalsToolbar
            tab={vm.tab}
            search={vm.search}
            category={vm.category}
            campusId={vm.campusId}
            sort={vm.sort}
            campuses={vm.campuses}
            disputeCount={vm.disputeCount}
            onChangeTab={vm.onChangeTab}
            onChangeSearch={vm.onChangeSearch}
            onChangeCategory={vm.onChangeCategory}
            onChangeCampus={vm.onChangeCampus}
            onChangeSort={vm.onChangeSort}
          />

          {vm.isLoading ? (
            <div className="flex min-h-64 items-center justify-center text-[13px] text-[var(--color-ink-muted)]">
              Loading {noun}s…
            </div>
          ) : vm.isError ? (
            <div className="flex min-h-64 flex-col items-center justify-center gap-2 p-6 text-center">
              <p className="text-sm font-semibold text-[var(--color-danger-ink)]">
                Couldn't load {noun}s
              </p>
              <p className="text-xs text-[var(--color-ink-muted)]">
                {vm.errorMessage ?? 'Please try again.'}
              </p>
            </div>
          ) : vm.total === 0 ? (
            <div className="flex min-h-64 flex-col items-center justify-center gap-2 p-6 text-center">
              <span className="text-3xl">{empty.icon}</span>
              <p className="text-sm font-semibold text-[var(--color-ink)]">{empty.title}</p>
              <p className="max-w-md text-xs text-[var(--color-ink-muted)]">{empty.sub}</p>
            </div>
          ) : vm.tab === 'active' ? (
            <ActiveRentalsTable bookings={vm.bookings} />
          ) : vm.tab === 'disputes' ? (
            <DisputesList disputes={vm.disputes} />
          ) : (
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 md:p-5 xl:grid-cols-3">
              {vm.listings.map((listing) => (
                <ListingCard
                  key={listing.id}
                  listing={listing}
                  mutating={vm.mutatingId === listing.id}
                  onView={vm.onView}
                  onRemove={vm.onRemove}
                  onRestore={vm.onRestore}
                />
              ))}
            </div>
          )}

          <footer className="flex flex-col gap-3 border-t border-[var(--color-divider)] px-4 py-3.5 md:flex-row md:items-center md:justify-between md:px-5">
            <div className="text-[12px] text-[var(--color-ink-muted)]">
              {vm.total === 0
                ? `No ${noun}s to show`
                : `Showing ${startIndex.toLocaleString()}–${endIndex.toLocaleString()} of ${vm.total.toLocaleString()} ${noun}${vm.total === 1 ? '' : 's'}`}
            </div>
            {vm.totalPages > 1 ? (
              <Pagination page={vm.page} totalPages={vm.totalPages} onChange={vm.onChangePage} />
            ) : null}
          </footer>
        </section>
      </div>

      <ListingDetailDrawer
        selectedListing={vm.selectedListing}
        detail={vm.detailData}
        isLoadingDetail={vm.isLoadingDetail}
        isErrorDetail={vm.isErrorDetail}
        mutatingId={vm.mutatingId}
        onClose={vm.onCloseDetail}
        onRemove={vm.onRemove}
        onRestore={vm.onRestore}
      />
    </>
  );
};

export default RentalsPage;
